// Sidebar.js

import React, { useRef, useEffect } from "react";
import { LayoutDashboard, Users, Bell, PieChart, FileText, Flag, Star, Layers, CreditCard, Briefcase, MessageSquare, Globe, Calendar, Sun, Image as ImageIcon, Music, Receipt, Smartphone } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

export default function Sidebar({ isOpen, onClose }) {
  const location = useLocation();
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      const savedScroll = sessionStorage.getItem('sidebarScroll');
      if (savedScroll) {
        scrollRef.current.scrollTop = parseInt(savedScroll, 10);
      }
    }
  }, []);

  const handleScroll = (e) => {
    sessionStorage.setItem('sidebarScroll', e.target.scrollTop);
  };

  const getNavItemClass = (path) => {
    const isActive = location.pathname === path;
    return isActive
      ? "flex items-center gap-3 p-3 rounded-xl bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 font-medium cursor-pointer transition-all"
      : "flex items-center gap-3 p-3 rounded-xl text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-slate-50 dark:hover:bg-slate-700/50 font-medium cursor-pointer transition-all";
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-40 lg:hidden transition-opacity"
          onClick={onClose}
        />
      )}

      {/* Sidebar Content */}
      <aside className={`w-64 bg-white dark:bg-slate-800 fixed h-screen z-50 flex flex-col border-r border-slate-200 dark:border-slate-700 shadow-sm transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="p-6 flex items-center border-b border-slate-200/60 dark:border-slate-700/60">
          <img
            src="/SocialBazarLogo.png"
            alt="SocialBazar Logo"
            className="h-16 w-auto object-contain drop-shadow-sm"
          />
          <h2 className="text-xl font-bold bg-gradient-to-r from-blue-900 to-blue-500 bg-clip-text text-transparent">
            SocialBaazar
          </h2>
        </div>

        <ul ref={scrollRef} onScroll={handleScroll} className="flex-1 px-4 mt-6 space-y-1 overflow-y-auto overflow-x-hidden pb-4">
          <li className="px-3 pb-2 pt-1 text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
            Main
          </li>
          <li>
            <Link to="/dashboard" className={getNavItemClass("/dashboard")}>
              <LayoutDashboard size={20} />
              <span>Dashboard</span>
            </Link>
          </li>
          <li>
            <Link to="/users" className={getNavItemClass("/users")}>
              <Users size={20} />
              <span>Users</span>
            </Link>
          </li>
          <li>
            <Link to="/user-groups" className={getNavItemClass("/user-groups")}>
              <Users size={20} />
              <span>User Groups</span>
            </Link>
          </li>

          <li className="px-3 pb-2 pt-4 text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
            Content
          </li>
          <li>
            <Link to="/festival-calendar" className={getNavItemClass("/festival-calendar")}>
              <Calendar size={20} />
              <span>Festival Calendar</span>
            </Link>
          </li>
          <li>
            <Link to="/daily-darshan" className={getNavItemClass("/daily-darshan")}>
              <Sun size={20} />
              <span>Daily Darshan</span>
            </Link>
          </li>
          <li>
            <Link to="/daily-content" className={getNavItemClass("/daily-content")}>
              <MessageSquare size={20} />
              <span>Daily Content</span>
            </Link>
          </li>
          <li>
            <Link to="/offer-banners" className={getNavItemClass("/offer-banners")}>
              <ImageIcon size={20} />
              <span>Offer Banners</span>
            </Link>
          </li>
          <li>
            <Link to="/splash-poster" className={getNavItemClass("/splash-poster")}>
              <Smartphone size={20} />
              <span>Splash Poster</span>
            </Link>
          </li>
          <li>
            <Link to="/music" className={getNavItemClass("/music")}>
              <Music size={20} />
              <span>Music</span>
            </Link>
          </li>

          <li className="px-3 pb-2 pt-4 text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
            Categories & Business
          </li>
          <li>
            <Link to="/categories" className={getNavItemClass("/categories")}>
              <Layers size={20} />
              <span>Categories</span>
            </Link>
          </li>
          <li>
            <Link to="/sub-category" className={getNavItemClass("/sub-category")}>
              <FileText size={20} />
              <span>Sub-Category</span>
            </Link>
          </li>
          <li>
            <Link to="/business-categories" className={getNavItemClass("/business-categories")}>
              <Briefcase size={20} />
              <span>Business Categories</span>
            </Link>
          </li>
          <li>
            <Link to="/politics-categories" className={getNavItemClass("/politics-categories")}>
              <Flag size={20} />
              <span>Politics Categories</span>
            </Link>
          </li>
          <li>
            <Link to="/business" className={getNavItemClass("/business")}>
              <Briefcase size={20} />
              <span>Business Management</span>
            </Link>
          </li>

          <li className="px-3 pb-2 pt-4 text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
            System & Settings
          </li>
          <li>
            <Link to="/languages" className={getNavItemClass("/languages")}>
              <Globe size={20} />
              <span>Languages</span>
            </Link>
          </li>
          <li>
            <Link to="/reviews-feedback" className={getNavItemClass("/reviews-feedback")}>
              <Star size={20} />
              <span>Reviews & Feedback</span>
            </Link>
          </li>
          <li>
            <Link to="/subscriptions" className={getNavItemClass("/subscriptions")}>
              <CreditCard size={20} />
              <span>Subscriptions</span>
            </Link>
          </li>
          <li>
            <Link to="/transactions" className={getNavItemClass("/transactions")}>
              <Receipt size={20} />
              <span>Transaction History</span>
            </Link>
          </li>
          <li>
            <Link to="/analytics" className={getNavItemClass("/analytics")}>
              <PieChart size={20} />
              <span>Analytics</span>
            </Link>
          </li>
          <li>
            <Link to="/notifications" className={getNavItemClass("/notifications")}>
              <Bell size={20} />
              <span>Notifications</span>
            </Link>
          </li>
        </ul>
      </aside>
    </>
  );
}
